<template>
  <div class="four-template">
    <!-- Основная секция: большое изображение слева и текст справа -->
    <div class="four-template__main-section">
      <div v-if="data.images.mainImage1" class="four-template__main-image">
        <TemplateMediaView
          :src="data.images.mainImage1"
          :aspect-width="456"
          :aspect-height="456"
        />
        <div v-if="data.images.mainImage2" class="four-template__main-subimage">
          <TemplateMediaView
            :src="data.images.mainImage2"
            :aspect-width="240"
            :aspect-height="240"
          />
        </div>
      </div>

      <div class="four-template__main-text">
        <BaseTypography with-pre-line type="h1" weight="600">
          {{ data.title }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.description }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.mainText"
          full-width
          :button="buttons.mainText"
        />
      </div>
    </div>

    <div class="four-template__second-header">
      <BaseTypography with-pre-line type="h3" weight="600">
        {{ data.titleHeaderBlock }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.descriptionHeaderBlock }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.secondHeader"
        :button="buttons.secondHeader"
      />
    </div>

    <!-- Три блока: изображение + текст под каждым -->
    <div class="four-template__triple-sections">
      <div class="four-template__section-item">
        <div class="four-template__section-image">
          <TemplateMediaView
            v-if="data.images.sectionImage1"
            :src="data.images.sectionImage1"
            :aspect-width="419"
            :aspect-height="280"
          />
        </div>
        <div class="four-template__section-text">
          <BaseTypography with-pre-line type="h3" weight="600">
            {{ data.firstSectionTitle }}
          </BaseTypography>
          <BaseTypography with-pre-line type="text" weight="400">
            {{ data.firstSectionDescription }}
          </BaseTypography>
          <TemplateLandingButtonView
            v-if="buttons.firstSection"
            full-width
            :button="buttons.firstSection"
          />
        </div>
      </div>
      <div class="four-template__section-item">
        <div class="four-template__section-image">
          <TemplateMediaView
            v-if="data.images.sectionImage2"
            :src="data.images.sectionImage2"
            :aspect-width="419"
            :aspect-height="280"
          />
        </div>
        <div class="four-template__section-text">
          <BaseTypography with-pre-line type="h3" weight="600">
            {{ data.secondSectionTitle }}
          </BaseTypography>
          <BaseTypography with-pre-line type="text" weight="400">
            {{ data.secondSectionDescription }}
          </BaseTypography>
          <TemplateLandingButtonView
            v-if="buttons.secondSection"
            full-width
            :button="buttons.secondSection"
          />
        </div>
      </div>
      <div class="four-template__section-item">
        <div class="four-template__section-image">
          <TemplateMediaView
            v-if="data.images.sectionImage3"
            :src="data.images.sectionImage3"
            :aspect-width="419"
            :aspect-height="280"
          />
        </div>
        <div class="four-template__section-text">
          <BaseTypography with-pre-line type="h3" weight="600">
            {{ data.thirdSectionTitle }}
          </BaseTypography>
          <BaseTypography with-pre-line type="text" weight="400">
            {{ data.thirdSectionDescription }}
          </BaseTypography>
          <TemplateLandingButtonView
            v-if="buttons.thirdSection"
            full-width
            :button="buttons.thirdSection"
          />
        </div>
      </div>
    </div>

    <!-- Дополнительный блок с заголовком и описанием -->
    <div class="four-template__additional-section">
      <BaseTypography with-pre-line type="h3" weight="600">
        {{ data.bottomSectionTitle }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.bottomSectionDescription }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.additional"
        :button="buttons.additional"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IFourTemplateData, TemplateData } from '~/entities/projects/model/templates-interface'
import { computed } from 'vue'
import { useTemplateViewButtons } from '~/entities/projects/model/useTemplateViewButtons'
import TemplateLandingButtonView from '~/entities/projects/ui/templates/TemplateLandingButtonView.vue'
import TemplateMediaView from '~/entities/projects/ui/templates/TemplateMediaView.vue'
import { BaseTypography } from '~/shared/ui'

interface Props {
  templateData?: TemplateData | null
}

const props = withDefaults(defineProps<Props>(), {
  templateData: null,
})

const data = computed(() => {
  if (props.templateData) {
    return props.templateData as IFourTemplateData
  }

  return {
    title: 'Как заполнить этот шаблон',
    description: 'Заголовок — напишите короткую фразу из 3–7 слов. Она должна быть простой и понятной. Заголовок должен передавать позитивное настроение. Например: про новый день, вдохновение, движение вперёд, радость, развитие. Не используйте сложные слова и длинные предложения.',
    titleHeaderBlock: 'Подзаголовок',
    descriptionHeaderBlock: 'Напишите текст объёмом до 1000 символов. Лучше разделить его на 2–3 абзаца, чтобы текст было удобно читать. Один абзац — это 2–4 предложения. Пишите простым языком. Представьте, что вы объясняете мысль другу. Текст должен быть лёгким, понятным и нейтральным. Он не должен быть привязан к конкретному продукту, компании или услуге. Это универсальный текст, который используется как пример для шаблона.',
    firstSectionTitle: 'Подзаголовок',
    firstSectionDescription: 'Можно писать о вдохновении, новых возможностях, развитии, спокойствии, ценности момента, движении вперёд или простых радостях жизни. Старайтесь: не использовать сложные профессиональные термины, не добавлять агрессивную рекламу, не писать слишком длинные предложения, не перегружать текст цифрами и фактами.',
    secondSectionTitle: 'Подзаголовок',
    secondSectionDescription: 'Важно, чтобы текст выглядел аккуратно в блоке и легко воспринимался при быстром чтении. Текст должен быть разделён на несколько аккуратных абзацев, чтобы его было удобно воспринимать визуально. Избегайте слишком длинных предложений и сложных конструкций.',
    thirdSectionTitle: 'Подзаголовок',
    thirdSectionDescription: 'Лучше выражать мысли коротко и ясно. Старайтесь, чтобы текст создавал лёгкое, позитивное настроение. Он может говорить о вдохновении, развитии, новых возможностях или ценности простых моментов. Главное — сохранить естественность и простоту.',
    bottomSectionTitle: 'Подзаголовок',
    bottomSectionDescription: 'Старайтесь не перегружать предложения и не делать их слишком длинными. Лучше разбивать текст на небольшие абзацы по несколько предложений, чтобы его было удобно читать даже при быстром просмотре страницы. Избегайте негативных формулировок, громких обещаний и излишнего пафоса. Текст должен выглядеть естественно и вызывать ощущение спокойствия и лёгкости. Если после прочтения создаётся ощущение ясности и простоты, значит текст подходит для шаблона. Главное — чтобы он был понятен любому человеку независимо от возраста и опыта.',
    images: {
      mainImage1: '/projects/project-template-5-bg-1.webp',
      mainImage2: '/projects/project-template-5-bg-2.webp',
      sectionImage1: '/projects/project-template-5-bg-3.webp',
      sectionImage2: '/projects/project-template-5-bg-4.webp',
      sectionImage3: '/projects/project-template-5-bg-5.webp',
    },
  }
})

const buttons = useTemplateViewButtons(() => props.templateData)
</script>

<style lang="scss" scoped>
.four-template {
  --main-image-max-width: 456px;
  --sub-image-max-width: 240px;
  --section-image-max-width: 100%;

  display: grid;
  gap: rem(60px);
  grid-template-columns: 1fr;

  @include bp-media('xs', 'sm', 'md') {
    gap: rem(40px);
  }

  @include bp-media('md') {
    --main-image-max-width: 377px;
    --sub-image-max-width: 189px;
  }

  @include bp-media('xs', 'sm') {
    --main-image-max-width: 274px;
    --sub-image-max-width: 120px;
  }

  > * {
    min-width: 0;
  }

  &__header {
    display: grid;
    justify-items: center;
    gap: rem(16px);
  }

  &__main-section {
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    gap: rem(32px);

    > * {
      min-width: 0;
    }

    @include bp-media('xs', 'sm', 'md', 'lg') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__second-header {
    display: flex;
    flex-direction: column;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

  &__main-image {
    display: grid;
    position: relative;
    width: 100%;
    max-width: 80%;
  }

  &__main-subimage {
    position: absolute;
    right: -8%;
    bottom: rem(20px);
    z-index: 1;
    width: 45%;
    max-width: var(--sub-image-max-width);
    min-width: rem(120px);
    overflow: hidden;
    border-radius: rem(16px);
  }

  &__main-text {
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: rem(16px);
    padding-top: rem(34px);
    min-width: 0;

    > * {
      min-width: 0;
      max-width: 100%;
    }

    &--sub {
      padding-top: 0;
    }

    @include bp-media('md') {
      padding-top: rem(24px);
    }

    @include bp-media('xs', 'sm') {
      padding-top: rem(40px);
    }
  }

  &__triple-sections {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: rem(32px);

    > * {
      min-width: 0;
    }

    @include bp-media('xs') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }

    @include bp-media('sm', 'md') {
      grid-template-columns: 1fr 1fr 1fr;
      gap: rem(24px);
    }
  }

  &__section-item {
    display: flex;
    flex-direction: column;
    gap: rem(16px);
    min-width: 0;

    > * {
      min-width: 0;
    }
  }

  &__section-image {
    overflow: hidden;
    border-radius: rem(16px);
    display: grid;
  }

  &__section-text {
    display: flex;
    flex-direction: column;
    gap: rem(8px);

    > * {
      min-width: 0;
    }
  }

  &__additional-section {
    display: flex;
    flex-direction: column;
    gap: rem(8px);

    > * {
      min-width: 0;
    }
  }

}
</style>
